// Zustand store for cart management and global state
import { create } from 'zustand';
import { Product, CartItem, Cart, Customer, StockValidationResult, SelectedOption, AppliedDiscount } from '../types';
import { storageService } from '../services/storage';
import { apiService, sendHeadsup } from '../services/api';
import { calculateItemUnitPrice, roundCurrency } from '../shared/pricing';
import { calculateDiscountAmount } from '../shared/discount';
import { getVisitorIdentitySnapshot } from '../services/identity';

interface CartStore {
    // Cart state
    cart: Cart;
    discountError: string | null;

    // Cart actions
    addToCart: (product: Product, quantity?: number, selectedOptions?: SelectedOption[]) => Promise<void>;
    removeFromCart: (cartItemIndex: number) => void;
    updateQuantity: (cartItemIndex: number, quantity: number) => Promise<void>;
    updateCartItem: (cartItemIndex: number, selectedOptions: SelectedOption[], quantity: number) => Promise<void>;
    clearCart: () => void;
    applyDiscount: (discount: AppliedDiscount) => void;
    clearDiscount: () => void;
    markDiscountStale: () => void;
    setDiscountError: (message: string | null) => void;
    validateAndCorrectCart: () => Promise<StockValidationResult | null>;

    // Customer state
    customer: Customer | null;
    setCustomer: (customer: Customer) => void;

    // Stock alerts
    stockAlert: StockValidationResult | null;
    setStockAlert: (alert: StockValidationResult | null) => void;

    // Initialization
    initializeFromStorage: () => void;
    syncCartPrices: (currentProducts: Product[]) => void;
}

const mapSelectedOptionsForPricing = (selectedOptions: SelectedOption[] = []) =>
    selectedOptions.map(option => ({
        optionId: option.optionId,
        choiceIds: option.choiceIds
    }));

// Helper function to check if two option sets are equivalent
const areOptionsEqual = (options1: SelectedOption[], options2: SelectedOption[]): boolean => {
    if (options1.length !== options2.length) return false;

    return options1.every(opt1 => {
        const matchingOpt2 = options2.find(opt2 => opt2.optionId === opt1.optionId);
        if (!matchingOpt2) return false;

        const sortedChoices1 = [...opt1.choiceIds].sort();
        const sortedChoices2 = [...matchingOpt2.choiceIds].sort();

        return sortedChoices1.length === sortedChoices2.length &&
            sortedChoices1.every((choice, index) => choice === sortedChoices2[index]);
    });
};

const calculateSubtotal = (items: CartItem[]): number => {
    const subtotal = items.reduce((sum, item) => sum + (item.totalPrice * item.quantity), 0);
    return roundCurrency(subtotal);
};

const normalizeDiscount = (discount: AppliedDiscount | null | undefined): AppliedDiscount | null => {
    if (!discount) {
        return null;
    }

    const percentage = Number(discount.percentage);
    const amount = Number(discount.amount);
    const validatedSubtotal = Number(discount.validatedSubtotal);
    const fixedAmount = discount.fixedAmount !== undefined ? Number(discount.fixedAmount) : undefined;
    const minOrderOverride = discount.minOrderOverride !== undefined ? Number(discount.minOrderOverride) : undefined;

    if (!Number.isFinite(percentage) || !Number.isFinite(amount) || !Number.isFinite(validatedSubtotal)) {
        return null;
    }

    return {
        code: String(discount.code || '').toUpperCase(),
        percentage: roundCurrency(percentage),
        fixedAmount: fixedAmount !== undefined && Number.isFinite(fixedAmount) ? roundCurrency(fixedAmount) : undefined,
        minOrderOverride: minOrderOverride !== undefined && Number.isFinite(minOrderOverride) ? roundCurrency(minOrderOverride) : undefined,
        amount: roundCurrency(Math.max(0, amount)),
        validatedSubtotal: roundCurrency(Math.max(0, validatedSubtotal)),
        validatedAt: discount.validatedAt || new Date().toISOString(),
        isStale: discount.isStale ?? false,
        message: discount.message
    };
};

const buildCart = (items: CartItem[], discountInput?: AppliedDiscount | null): Cart => {
    const subtotal = calculateSubtotal(items);
    let appliedDiscount = normalizeDiscount(discountInput);
    let discountAmount = 0;

    if (appliedDiscount) {
        const difference = Math.abs(appliedDiscount.validatedSubtotal - subtotal);
        const shouldMarkStale = difference > 0.01;

        if (shouldMarkStale && !appliedDiscount.isStale) {
            appliedDiscount = { ...appliedDiscount, isStale: true };
        }

        // Always recalculate discount using the shared function for consistency with server
        // This ensures client and server use identical calculation logic
        // Uses combined fixed + percentage calculation
        discountAmount = calculateDiscountAmount(subtotal, {
            fixedAmount: appliedDiscount.fixedAmount,
            percentage: appliedDiscount.percentage
        });
    }

    const total = roundCurrency(Math.max(0, subtotal - discountAmount));

    return {
        items,
        totals: {
            subtotal,
            discountAmount,
            total
        },
        discount: appliedDiscount,
        total // legacy support for existing consumers
    };
};

const normalizeStoredCart = (rawCart: any): Cart => {
    if (!rawCart || typeof rawCart !== 'object') {
        return buildCart([]);
    }

    const items = Array.isArray(rawCart.items) ? rawCart.items : [];
    const discount = normalizeDiscount(rawCart.discount);
    return buildCart(items, discount);
};

const emptyCart = buildCart([]);

/**
 * Send headsup notification for basket changes.
 * Fire-and-forget, wrapped in try-catch for safety.
 */
const sendBasketHeadsup = (cart: Cart): void => {
    try {
        if (cart.items.length === 0) {
            return; // Don't send headsup for empty basket
        }

        const visitorIdentity = getVisitorIdentitySnapshot();
        const savedCustomer = storageService.getCustomer();
        const hasUserInfo = !!(savedCustomer?.name || savedCustomer?.address);

        sendHeadsup({
            eventType: 'basket_changed',
            visitorId: visitorIdentity?.id,
            customerName: savedCustomer?.name || undefined,
            customerAddress: savedCustomer?.address || undefined,
            cartItems: cart.items,
            // Only include visit count if user doesn't have previous order info
            visitCount: !hasUserInfo ? visitorIdentity?.visitCount : undefined,
        });
    } catch (error) {
        // Silently ignore - headsup is non-critical
        console.log('[Headsup] Error sending basket headsup, continuing...', error);
    }
};

export const useCartStore = create<CartStore>((set, get) => ({
    // Initial state
    cart: emptyCart,
    customer: null,
    stockAlert: null,
    discountError: null,

    // Cart actions
    addToCart: async (product: Product, quantity = 1, selectedOptions: SelectedOption[] = []) => {
        set((state) => {
            // Calculate total price including options
            const { unitPrice, errors: pricingErrors } = calculateItemUnitPrice(
                product,
                mapSelectedOptionsForPricing(selectedOptions)
            );

            if (pricingErrors.length > 0) {
                console.warn('Pricing discrepancies detected while adding to cart:', pricingErrors);
            }

            const totalPrice = unitPrice;

            // Find existing item with same product and options
            const existingItemIndex = state.cart.items.findIndex(item =>
                item.product.id === product.id && areOptionsEqual(item.selectedOptions, selectedOptions)
            );

            let newItems: CartItem[];
            if (existingItemIndex !== -1) {
                // Update existing item quantity
                newItems = state.cart.items.map((item, index) =>
                    index === existingItemIndex
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                );
            } else {
                // Add new item with options
                const newItem: CartItem = {
                    product,
                    quantity,
                    selectedOptions,
                    totalPrice
                };
                newItems = [...state.cart.items, newItem];
            }

            const nextCart = buildCart(newItems, state.cart.discount);

            // Persist to storage
            storageService.setCart(nextCart);

            return { cart: nextCart, discountError: null };
        });

        // Send headsup notification for basket change
        sendBasketHeadsup(get().cart);

        // Validate cart after adding item
        await get().validateAndCorrectCart();
    },

    removeFromCart: (cartItemIndex: number) => {
        set((state) => {
            const newItems = state.cart.items.filter((_, index) => index !== cartItemIndex);
            const nextCart = buildCart(newItems, state.cart.discount);

            // Persist to storage
            storageService.setCart(nextCart);

            return { cart: nextCart };
        });
    },

    updateQuantity: async (cartItemIndex: number, quantity: number) => {
        if (quantity <= 0) {
            // Remove item if quantity is 0 or negative
            get().removeFromCart(cartItemIndex);
            return;
        }

        set((state) => {
            const newItems = state.cart.items.map((item, index) =>
                index === cartItemIndex
                    ? { ...item, quantity }
                    : item
            );

            const nextCart = buildCart(newItems, state.cart.discount);

            // Persist to storage
            storageService.setCart(nextCart);

            return { cart: nextCart };
        });

        // Send headsup notification for basket change
        sendBasketHeadsup(get().cart);

        // Validate cart after updating quantity
        await get().validateAndCorrectCart();
    },

    updateCartItem: async (cartItemIndex: number, selectedOptions: SelectedOption[], quantity: number) => {
        if (quantity <= 0) {
            get().removeFromCart(cartItemIndex);
            return;
        }

        set((state) => {
            const existingItem = state.cart.items[cartItemIndex];
            if (!existingItem) {
                return {};
            }

            // Calculate new total price with updated options
            const { unitPrice, errors: pricingErrors } = calculateItemUnitPrice(
                existingItem.product,
                mapSelectedOptionsForPricing(selectedOptions)
            );

            if (pricingErrors.length > 0) {
                console.warn('Pricing discrepancies detected while updating cart item:', pricingErrors);
            }

            const newItems = state.cart.items.map((item, index) =>
                index === cartItemIndex
                    ? { ...item, selectedOptions, quantity, totalPrice: unitPrice }
                    : item
            );

            const nextCart = buildCart(newItems, state.cart.discount);

            // Persist to storage
            storageService.setCart(nextCart);

            return { cart: nextCart };
        });

        // Send headsup notification for basket change
        sendBasketHeadsup(get().cart);

        // Validate cart after updating item
        await get().validateAndCorrectCart();
    },

    clearCart: () => {
        storageService.clearCart();
        set({ cart: emptyCart, discountError: null });
    },

    applyDiscount: (discount: AppliedDiscount) => {
        const normalized = normalizeDiscount(discount);
        set((state) => {
            const nextCart = buildCart(state.cart.items, normalized ? { ...normalized, isStale: false } : null);
            storageService.setCart(nextCart);
            return { cart: nextCart, discountError: null };
        });
    },

    clearDiscount: () => {
        set((state) => {
            if (!state.cart.discount) {
                return {};
            }

            const nextCart = buildCart(state.cart.items, null);
            storageService.setCart(nextCart);
            return { cart: nextCart, discountError: null };
        });
    },

    markDiscountStale: () => {
        set((state) => {
            if (!state.cart.discount || state.cart.discount.isStale) {
                return {};
            }

            const nextCart = buildCart(state.cart.items, { ...state.cart.discount, isStale: true });
            storageService.setCart(nextCart);
            return { cart: nextCart };
        });
    },

    setDiscountError: (message: string | null) => {
        set({ discountError: message });
    },

    validateAndCorrectCart: async () => {
        const currentCart = get().cart;

        if (currentCart.items.length === 0) {
            get().setStockAlert(null);
            get().setDiscountError(null);
            return null;
        }

        try {
            const validationResult = await apiService.validateCart(currentCart);

            if (!validationResult.isValid) {
                // Update cart with corrected version
                const nextCart = buildCart(validationResult.correctedCart.items, currentCart.discount ? { ...currentCart.discount, isStale: true } : null);
                const nextAlert: StockValidationResult = {
                    ...validationResult,
                    correctedCart: nextCart
                };

                set({ cart: nextCart, stockAlert: nextAlert });
                storageService.setCart(nextCart);

                return nextAlert;
            }

            // Clear alert if cart is valid
            get().setStockAlert(null);
            get().setDiscountError(null);
            return null; // Cart is valid, no changes needed
        } catch (error) {
            console.error('Failed to validate cart:', error);
            get().setStockAlert(null);
            return null;
        }
    },

    // Customer actions
    setCustomer: (customer: Customer) => {
        storageService.setCustomer(customer);
        set({ customer });
    },

    setStockAlert: (alert: StockValidationResult | null) => {
        set({ stockAlert: alert });
    },

    // Initialize from storage
    initializeFromStorage: () => {
        const savedCart = normalizeStoredCart(storageService.getCart());
        const savedCustomer = storageService.getCustomer();

        set({
            cart: savedCart,
            customer: savedCustomer
        });
    },

    // Sync cart item prices with current product prices
    syncCartPrices: (currentProducts: Product[]) => {
        const currentCart = get().cart;
        if (currentCart.items.length === 0 || currentProducts.length === 0) {
            return;
        }

        const productMap = new Map(currentProducts.map(p => [p.id, p]));
        let hasChanges = false;

        const updatedItems = currentCart.items.map((item) => {
            const currentProduct = productMap.get(item.product.id);
            if (!currentProduct) {
                return item; // Product no longer exists — leave as-is, validateAndCorrectCart handles removal
            }

            // Check if the base price has changed
            if (item.product.price === currentProduct.price) {
                return item;
            }

            // Recalculate totalPrice using the current product price + selected options
            const { unitPrice } = calculateItemUnitPrice(
                currentProduct,
                mapSelectedOptionsForPricing(item.selectedOptions)
            );

            console.log(`[PRICE-SYNC] ${currentProduct.name}: £${item.product.price} → £${currentProduct.price} (total: £${item.totalPrice} → £${unitPrice})`);
            hasChanges = true;

            return {
                ...item,
                product: { ...item.product, price: currentProduct.price },
                totalPrice: unitPrice
            };
        });

        if (hasChanges) {
            const nextCart = buildCart(updatedItems, currentCart.discount);
            storageService.setCart(nextCart);
            set({ cart: nextCart });
        }
    },
}));
